import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { getToken, setToken, clearToken, enterImpersonationToken, exitImpersonationToken } from "@/lib/token";

const AuthContext = createContext(null);

const DEFAULT_BRAND = "#1E40AF";

function applyBrand(branding) {
  const color = branding?.brand_color || DEFAULT_BRAND;
  const header = branding?.header_color || color;
  const root = document.documentElement;
  root.style.setProperty("--brand", color);
  root.style.setProperty("--brand-header", header);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [company, setCompany] = useState(null);
  const [branding, setBranding] = useState(null);
  const [loading, setLoading] = useState(true);

  const hydrate = useCallback((data) => {
    setUser(data.user);
    setCompany(data.company || null);
    setBranding(data.branding || null);
    applyBrand(data.branding);
  }, []);

  useEffect(() => {
    const token = getToken();
    if (!token) {
      setLoading(false);
      return;
    }
    api
      .get("/auth/me")
      .then((r) => hydrate(r.data))
      .catch(() => clearToken())
      .finally(() => setLoading(false));
  }, [hydrate]);

  const login = async (username, password, remember = false) => {
    const r = await api.post("/auth/login", { username, password, remember });
    setToken(r.data.token, remember);
    hydrate(r.data);
    return r.data.user;
  };

  const logout = () => {
    clearToken();
    setUser(null);
    setCompany(null);
    setBranding(null);
    applyBrand(null);
  };

  const refreshBranding = (b) => {
    setBranding(b);
    applyBrand(b);
  };

  const refreshMe = useCallback(async () => {
    const r = await api.get("/auth/me");
    hydrate(r.data);
  }, [hydrate]);

  const enterImpersonation = async (token) => {
    enterImpersonationToken(token);
    const r = await api.get("/auth/me");
    hydrate(r.data);
  };

  const exitImpersonation = async () => {
    exitImpersonationToken();
    const r = await api.get("/auth/me");
    hydrate(r.data);
  };

  return (
    <AuthContext.Provider value={{ user, company, branding, loading, login, logout, refreshBranding, refreshMe, setCompany, enterImpersonation, exitImpersonation }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
